import type { WantedProfile } from "@asap/shared/schemas/wanted-profile";
import {
  EmployeeStatusSchema,
  WantedProfileStatusSchema,
  type EmployeeStatus,
  type WantedProfileStatus,
} from "@asap/shared/types/supabase.types.gen";
import { translateEmployeeStatus } from "@asap/shared/utils/translate";
import type { TableRowAction } from "~/components/shared/table/TableRowActions.vue";
import { useWantedProfileApi } from "~/components/wanted-profile/composables/useWantedProfileApi";
import {
  buildWantedProfileListQuery,
  getLastPageIndex,
  getStatusAfterToggle,
  getWantedProfileEmptyStateType,
  wantedProfileQualificationItems,
  wantedProfileStatuses,
  type WantedProfileTableFilters,
} from "~/components/wanted-profile/wanted-profile.model";
import { useToast } from "~/composables/useToast";
import type { TableEmptyStateType } from "~/utils/types";

export const useWantedProfileList = (companyId: () => string) => {
  const { t } = useI18n();
  const { addToast, addToastError } = useToast();
  const { list, update } = useWantedProfileApi();

  const filters = reactive<WantedProfileTableFilters>({
    search: undefined,
    agencyId: undefined,
    employeeStatus: undefined,
    status: undefined,
    pageSize: 20,
    pageIndex: 0,
  });
  const wantedProfiles = ref<WantedProfile[]>([]);
  const totalItems = ref(0);
  const isLoading = ref(true);
  const emptyStateType = ref<TableEmptyStateType>("no-data");
  const isSheetOpen = ref(false);
  const wantedProfileToEdit = ref<WantedProfile>();
  const wantedProfileIdsBeingUpdated = new Set<string>();
  let latestFetchId = 0;

  const statusLabels = computed(() => ({
    open: t("wantedProfiles.status.open"),
    covered: t("wantedProfiles.status.covered"),
    archived: t("wantedProfiles.status.archived"),
  }));

  const getStatusLabel = (status: WantedProfileStatus) => statusLabels.value[status];

  const statusItems = computed(() =>
    wantedProfileStatuses.map((status) => ({ value: status, label: statusLabels.value[status] }))
  );

  const qualificationItems = wantedProfileQualificationItems;

  const getQualificationLabel = (employeeStatus: EmployeeStatus | null) =>
    employeeStatus ? translateEmployeeStatus.fr[employeeStatus] : "-";

  const fetchWantedProfiles = async () => {
    const fetchId = ++latestFetchId;
    isLoading.value = true;
    try {
      const wantedProfilesAnswer = await list(buildWantedProfileListQuery(companyId(), filters));
      if (fetchId !== latestFetchId) return;
      wantedProfiles.value = wantedProfilesAnswer.items;
      totalItems.value = wantedProfilesAnswer.totalItems;
      const isPastLastPage = wantedProfilesAnswer.items.length === 0 && wantedProfilesAnswer.totalItems > 0;
      if (isPastLastPage && filters.pageIndex > 0) {
        filters.pageIndex = getLastPageIndex(wantedProfilesAnswer.totalItems, filters.pageSize);
        return;
      }
      emptyStateType.value = getWantedProfileEmptyStateType(filters);
    } catch (error) {
      if (fetchId !== latestFetchId) return;
      emptyStateType.value = "error";
      addToastError({ title: t("wantedProfiles.list.loadError") }, error);
    } finally {
      if (fetchId === latestFetchId) isLoading.value = false;
    }
  };

  const rowActionByTargetStatus = computed(
    () =>
      ({
        covered: {
          label: t("wantedProfiles.rowActions.markAsCovered"),
          icon: "i-ph-check-circle",
          doneTitle: t("wantedProfiles.rowActions.markAsCoveredDone"),
        },
        open: {
          label: t("wantedProfiles.rowActions.reopen"),
          icon: "i-ph-arrows-counter-clockwise",
          doneTitle: t("wantedProfiles.rowActions.reopenDone"),
        },
      }) satisfies Record<"open" | "covered", { label: string; icon: string; doneTitle: string }>
  );

  const updateStatus = async (wantedProfile: WantedProfile, targetStatus: "open" | "covered") => {
    if (wantedProfileIdsBeingUpdated.has(wantedProfile.id)) return;
    wantedProfileIdsBeingUpdated.add(wantedProfile.id);
    try {
      await update(wantedProfile.id, { status: targetStatus });
      addToast({ title: rowActionByTargetStatus.value[targetStatus].doneTitle });
      await fetchWantedProfiles();
    } catch (error) {
      addToastError({ title: t("wantedProfiles.rowActions.updateError") }, error);
    } finally {
      wantedProfileIdsBeingUpdated.delete(wantedProfile.id);
    }
  };

  const openEditSheet = (wantedProfile: WantedProfile) => {
    wantedProfileToEdit.value = wantedProfile;
    isSheetOpen.value = true;
  };

  const openCreateSheet = () => {
    wantedProfileToEdit.value = undefined;
    isSheetOpen.value = true;
  };

  const closeSheet = () => {
    isSheetOpen.value = false;
    wantedProfileToEdit.value = undefined;
  };

  const handleSheetSaved = () => {
    closeSheet();
    fetchWantedProfiles();
  };

  const getEditRowAction = (wantedProfile: WantedProfile): TableRowAction => ({
    label: t("wantedProfiles.rowActions.edit"),
    icon: "i-ph-pencil-simple",
    onClick: () => openEditSheet(wantedProfile),
  });

  const getPrimaryRowAction = (wantedProfile: WantedProfile): TableRowAction => {
    const targetStatus = getStatusAfterToggle(wantedProfile.status);
    const { label, icon } = rowActionByTargetStatus.value[targetStatus];
    return { label, icon, onClick: () => updateStatus(wantedProfile, targetStatus) };
  };

  const getSecondaryRowActions = (wantedProfile: WantedProfile) => [getEditRowAction(wantedProfile)];

  const setStatusFilter = (value: string | string[]) => {
    filters.status = WantedProfileStatusSchema.safeParse(value).data;
  };

  const setEmployeeStatusFilter = (value: string | string[]) => {
    filters.employeeStatus = EmployeeStatusSchema.safeParse(value).data;
  };

  const resetFilters = () => {
    filters.search = undefined;
    filters.agencyId = undefined;
    filters.employeeStatus = undefined;
    filters.status = undefined;
  };

  onMounted(() => {
    fetchWantedProfiles();
  });

  watch(
    () => filters.pageIndex,
    () => {
      fetchWantedProfiles();
    }
  );

  watch(
    [
      () => filters.search,
      () => filters.agencyId,
      () => filters.employeeStatus,
      () => filters.status,
      () => filters.pageSize,
    ],
    () => {
      if (filters.pageIndex !== 0) {
        filters.pageIndex = 0;
        return;
      }
      fetchWantedProfiles();
    }
  );

  return {
    filters,
    wantedProfiles,
    totalItems,
    statusItems,
    getStatusLabel,
    qualificationItems,
    getQualificationLabel,
    isLoading,
    emptyStateType,
    isSheetOpen,
    wantedProfileToEdit,
    openCreateSheet,
    closeSheet,
    handleSheetSaved,
    openEditSheet,
    getPrimaryRowAction,
    getSecondaryRowActions,
    setStatusFilter,
    setEmployeeStatusFilter,
    resetFilters,
  };
};
